import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private darkMode = new BehaviorSubject<boolean>(false);
  private readonly THEME_KEY = 'rarr-theme';

  constructor() {
    this.initializeTheme();
  }

  private initializeTheme(): void {
    // Check if user has a saved preference
    const savedTheme = localStorage.getItem(this.THEME_KEY);
    
    if (savedTheme) {
      // Use saved preference
      const isDark = savedTheme === 'dark';
      this.setDarkMode(isDark);
    } else {
      // Detect system dark mode preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.setDarkMode(prefersDark);
    }

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      // Only auto-switch if user hasn't set a manual preference
      if (!localStorage.getItem(this.THEME_KEY)) {
        this.setDarkMode(e.matches);
      }
    });
  }

  setDarkMode(isDark: boolean, savePreference: boolean = true): void {
    this.darkMode.next(isDark);
    
    // Temporarily disable transitions to prevent flash
    document.body.classList.add('navigation-transition');
    
    // Apply theme to document root using data-theme attribute
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    
    // Also maintain body class for backward compatibility
    if (isDark) {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }

    // Re-enable transitions after theme is applied
    setTimeout(() => {
      document.body.classList.remove('navigation-transition');
    }, 100);

    // Save user preference
    if (savePreference) {
      localStorage.setItem(this.THEME_KEY, isDark ? 'dark' : 'light');
    }
  }

  toggleTheme(): void {
    const currentTheme = this.darkMode.value;
    this.setDarkMode(!currentTheme);
  }

  get isDarkMode$(): Observable<boolean> {
    return this.darkMode.asObservable();
  }

  get isDarkMode(): boolean {
    return this.darkMode.value;
  }

  getCurrentTheme(): 'light' | 'dark' {
    return this.darkMode.value ? 'dark' : 'light';
  }

  clearThemePreference(): void {
    localStorage.removeItem(this.THEME_KEY);
    // Revert to system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.setDarkMode(prefersDark, false);
  }
}